import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dateOnly, todayISO } from '../../lib/format';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot } from '../ui/Page';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { dash } from '@/components/ui/data-table';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState, GoneState, isNotFound } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { LineItemsTable } from '../money/LineItemsTable';
import { BackToJobButton, goesBackToJob } from '../money/BackToJobButton';
import { ShowHoursBar } from '../money/ShowHoursBar';
import { lineBadges, unpricedLines } from '../money/lineNotes';
import { IssueAnyway, IssueButton, issueWarningLines } from '../money/IssueWarning';
import { RecordPaymentCard } from '../money/RecordPaymentCard';
import { InvoicePaymentsCard } from '../money/InvoicePaymentsCard';
import { DocumentLink } from '../money/DocumentLink';
import { CustomerLinkRow } from '../ui/CustomerLinkRow';
import {
  CUSTOMER_EMAIL_POLL_MS,
  CustomerEmailLine,
  customerEmailPending,
  sendButtonState,
} from '../ui/CustomerEmailLine';
import { ProjectLinkCard } from '../projects/ProjectLinkCard';
import {
  InvoiceCollectionsCard,
  NextLateFeeNotice,
} from '../collections/InvoiceCollectionsCard';
import { can } from '@servwell/shared';
import { useSession } from '../session';
import { INVOICE_STATUS, display } from '../status';
import { money, daysOverdue, invoiceBalanceCents, balanceLabel } from '../money';
import { IconMoney } from '../icons';
import { WithOrgClock } from '../ui/WithOrgClock';

/**
 * One invoice, addressable (router law). A draft is still editable-in-principle and
 * invisible to accounting; issuing assigns the number and freezes it, which is why
 * "Issue" is a deliberate button and not a side effect of creating the invoice.
 */
export function InvoiceDetailPage() {
  return (
    <WithOrgClock what="this invoice" icon={IconMoney} title="Invoice">
      {(tz) => <InvoiceDetail tz={tz} />}
    </WithOrgClock>
  );
}

function InvoiceDetail({ tz }: { tz: string }) {
  const today = todayISO(tz);
  const { invoiceId } = useParams({ from: '/office/invoices/$invoiceId' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role } = useSession();
  const canBill = can(role, 'invoice', 'update');
  const q = useQuery({
    ...trpc.invoice.get.queryOptions({ invoiceId }),
    // The email reads "Sending…" until the drain answers (MAP 249): ask again while it does.
    refetchInterval: (query) =>
      customerEmailPending(query.state.data?.email) ? CUSTOMER_EMAIL_POLL_MS : false,
  });
  const customer = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: q.data?.invoice.customerId ?? '' }),
    enabled: Boolean(q.data?.invoice.customerId),
  });
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  // Issue on a draft with a line to check asks "Issue anyway?" first (Q5): warns, never blocks.
  const [askingToIssue, setAskingToIssue] = useState(false);
  const issueButton = useRef<HTMLButtonElement>(null);
  // Closing the ask hands focus back to Issue, never to the page (keyboard baseline).
  const keepChecking = () => {
    setAskingToIssue(false);
    requestAnimationFrame(() => issueButton.current?.focus());
  };
  // Fixed meanwhile (a refetch came back clean): the ask is over, so a later flag can't
  // reopen it by itself.
  const asksFirst = issueWarningLines(q.data?.issueWarnings ?? null).length > 0;
  useEffect(() => {
    if (askingToIssue && !asksFirst) setAskingToIssue(false);
  }, [askingToIssue, asksFirst]);

  const refresh = () => void qc.invalidateQueries();
  const issue = useMutation(
    trpc.invoice.issue.mutationOptions({
      onSuccess: () => {
        setAskingToIssue(false);
        refresh();
      },
    }),
  );
  // The answer names the queued email; what became of it is read back from the invoice
  // (`email`, from the outbox), so a reload, another tab and the drain all agree.
  const send = useMutation(trpc.invoice.send.mutationOptions({ onSuccess: refresh }));
  const voidIt = useMutation(
    trpc.invoice.void.mutationOptions({
      onSuccess: () => {
        setVoiding(false);
        refresh();
      },
    }),
  );

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <BackCrumb to="/money" label="Money" />
        {/* Deleted, or an old link — gone is not broken, and there is nothing to retry. */}
        {isNotFound(q.error) ? (
          <GoneState
            icon={IconMoney}
            what="invoice"
            backTo="/money"
            backLabel="Back to Money"
          />
        ) : (
          <ErrorState
            icon={IconMoney}
            what="this invoice"
            onRetry={() => void q.refetch()}
          />
        )}
      </PageRoot>
    );
  }
  if (!q.data) {
    return (
      <PageRoot>
        <BackCrumb to="/money" label="Money" />
        <EmptyState
          icon={IconMoney}
          title="Invoice not found"
          description="It may have been deleted, or the link is stale."
        />
      </PageRoot>
    );
  }

  const {
    invoice,
    lines,
    lineNotes,
    issueWarnings,
    fees,
    reminders,
    nextFee,
    publicUrl,
    email,
  } = q.data;
  const issueIt = () => issue.mutate({ invoiceId, ...idem() });
  const s = display(INVOICE_STATUS, invoice.status);
  // By id. Scanning the whole customer list for one name meant anyone past the 500-row
  // cap rendered as an em-dash on their own invoice.
  const customerRow = customer.data?.customer;
  const customerName = customerRow?.displayName ?? null;
  const balanceCents = invoiceBalanceCents(invoice);
  const late = daysOverdue(invoice.dueDate, today);
  const isLate =
    (late ?? -1) > 0 && invoice.status !== 'paid' && invoice.status !== 'void';

  const error = issue.error ?? voidIt.error ?? send.error;
  // Off while an email is on its way (MAP 249, Codex r1 #4), whatever this tab last pressed.
  const sendButton = sendButtonState({
    email,
    sentAt: invoice.sentAt,
    pressing: send.isPending,
  });

  return (
    <PageRoot>
      <BackCrumb to="/money" label="Money" />
      <DetailHeader
        title={
          invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : 'Draft invoice'
        }
        badge={
          <StatusBadge tone={isLate ? 'danger' : s.tone}>
            {isLate ? `${late}d late` : s.label}
          </StatusBadge>
        }
        right={
          <>
            {/* A draft has no number yet and shouldn't leave the building, so the PDF
                appears the moment the invoice becomes real. This is the button the send
                notice below has been telling people to press. */}
            {invoice.status !== 'draft' ? (
              <DocumentLink kind="invoice" id={invoiceId} label="PDF" />
            ) : null}
            {invoice.status === 'draft' ? (
              <IssueButton
                warnings={issueWarnings}
                pending={issue.isPending}
                onIssue={issueIt}
                onAsk={() => setAskingToIssue(true)}
                buttonRef={issueButton}
              />
            ) : null}
            {invoice.status === 'issued' || invoice.status === 'partially_paid' ? (
              <>
                <Button
                  disabled={sendButton.disabled}
                  onClick={() => send.mutate({ invoiceId, ...idem() })}
                >
                  {sendButton.label}
                </Button>
                <Button variant="ghost" onClick={() => setVoiding((v) => !v)}>
                  Void
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <MetaStrip
        items={[
          { label: 'Customer', value: dash(customerName) },
          {
            label: 'Due',
            value: dash(invoice.dueDate ? dateOnly(invoice.dueDate) : null),
          },
          { label: 'Total', value: money(invoice.totalCents) },
          { label: 'Balance', value: balanceLabel(balanceCents) },
        ]}
      />

      {askingToIssue && invoice.status === 'draft' ? (
        <IssueAnyway
          warnings={issueWarnings}
          pending={issue.isPending}
          onIssue={issueIt}
          onKeep={keepChecking}
        />
      ) : null}

      {error ? <Notice tone="danger">{error.message}</Notice> : null}

      {/* What became of the latest send, from the outbox (MAP 249): Sending…, Sent, Waiting:
          … or Didn't send: …, in the words that match the truth. */}
      {email ? (
        <CustomerEmailLine email={email}>
          The link below shows them the work, what they have paid, and what is left — so
          it stays right even when this does not.
        </CustomerEmailLine>
      ) : null}

      {/* An issued invoice can be looked at by the person who has to pay it. Sits directly
          under the send notice, because "nothing was sent" is exactly when somebody needs
          the link they can paste somewhere themselves. */}
      {invoice.status !== 'draft' ? (
        <SectionCard title="Link for the customer">
          <p className="mb-2 text-sm text-muted-foreground">
            Opens a page showing this invoice — the work, what&apos;s been paid, and
            what&apos;s left. Anyone with the link can see it, so treat it like the
            invoice itself.
          </p>
          <CustomerLinkRow kind="invoice" id={invoiceId} url={publicUrl} />
        </SectionCard>
      ) : null}

      {/* Invoicing is where a project's revenue actually lands, so the association has to be
          settable here too — not only on the job it came from. */}
      <ProjectLinkCard
        entityType="invoice"
        entityId={invoiceId}
        customerId={invoice.customerId}
        projectId={invoice.projectId}
      />

      <NextLateFeeNotice tz={tz} nextFee={nextFee ?? null} />

      {/* Money arrives at the office as often as at the door — a cheque in the post, a
          transfer, a card over the counter. Until now only the tech's phone could record
          any of it. */}
      {balanceCents > 0 && invoice.status !== 'void' && invoice.status !== 'draft' ? (
        <RecordPaymentCard tz={tz} invoiceId={invoiceId} balanceCents={balanceCents} />
      ) : null}

      <InvoicePaymentsCard invoiceId={invoiceId} />

      {voiding ? (
        <SectionCard title="Void this invoice">
          <p className="mb-2 text-sm text-muted-foreground">
            An issued invoice is never edited or deleted — it's voided, and the reason
            stays on the record. Write a new one to replace it.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2.5 py-1.5 text-sm"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Why is it being voided?"
              aria-label="Reason for voiding"
              autoFocus
            />
            <Button
              variant="danger"
              onClick={() =>
                voidIt.mutate({ invoiceId, reason: voidReason.trim(), ...idem() })
              }
              disabled={voidIt.isPending || voidReason.trim().length === 0}
            >
              {voidIt.isPending ? 'Voiding…' : 'Void invoice'}
            </Button>
            <Button variant="ghost" onClick={() => setVoiding(false)}>
              Cancel
            </Button>
          </div>
        </SectionCard>
      ) : null}

      {invoice.status === 'draft' ? (
        <Notice tone="info">
          This is a draft — the customer can't see it and it has no number yet. Issuing it
          assigns the number and locks the amounts.
        </Notice>
      ) : null}

      <SectionCard
        title={`Line items (${lines.length})`}
        right={
          invoice.jobId ? (
            <Link
              to="/jobs/$jobId"
              params={{ jobId: invoice.jobId }}
              className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
            >
              See the job
            </Link>
          ) : null
        }
      >
        {/* Ticket 13: the customer sees the labor charge, not the hours, unless shown. */}
        <ShowHoursBar invoice={invoice} lines={lines} canSwitch={canBill} />
        <LineItemsTable
          lines={lines}
          subtotalCents={invoice.subtotalCents}
          taxCents={invoice.taxCents}
          totalCents={invoice.totalCents}
          paidCents={invoice.amountPaidCents}
          lateFeeCents={invoice.lateFeeCents}
          badges={lineBadges(lineNotes, tz)}
          unpriced={unpricedLines(lineNotes)}
          // A draft is never edited by hand (12b, P10): a part, charge or time line goes back to the job.
          action={
            invoice.status === 'draft' && canBill
              ? (l) => {
                  const line = lines.find((x) => x.id === l.id);
                  return line && goesBackToJob(line) ? (
                    <BackToJobButton lineId={l.id} name={l.name} />
                  ) : null;
                }
              : undefined
          }
        />
      </SectionCard>

      <InvoiceCollectionsCard fees={fees ?? []} reminders={reminders ?? []} />
    </PageRoot>
  );
}
